import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import LiveCourseCard from './LiveCourseCard';
import ClosestEventCard from '../Card/ClosestEventCard';
import { getCourses } from '@/services/strapiService';
import type { Course, LiveCourse } from '@/types/course';
import Loader from '../Loader/Loader';
import BookingModal from '../Modal/BookingModal';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import { T } from '@tolgee/react';
import { mapCourseToLiveCourse } from '@/components/Slider/LiveCourseSlider';

const LiveCoursesSection: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<LiveCourse | null>(null);
  const locale = useSelectedLanguage();

  const openModal = (course: LiveCourse, isSuccess?: boolean) => {
    setSelectedCourse(course);
    isSuccess && setIsSuccess(isSuccess);
  };

  const closeModal = () => {
    setSelectedCourse(null);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await getCourses(4, ['date:asc'], 0, locale);
        setCourses(response.data);
        setLoading(false);
      } catch (_error) {
        console.error('Error fetching live courses');
        setLoading(false);
      }
    };

    void fetchCourses();
  }, [locale]);

  const handleCourseCardClick = (course: Course) => {
    openModal(mapCourseToLiveCourse(course));
  };

  if (loading) {
    return <Loader position={'absolute'} />;
  }

  if (!courses || courses.length === 0) {
    return <p>No live courses available.</p>;
  }

  return (
    <div className="relative mt-8 bg-gray p-5 md:p-12">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-6xl">
          <T>home.live_class_section.title.label</T>
        </h2>
        <Link href="/live-courses" className="text-primary text-sm" passHref prefetch={true}>
          <T>home.live_class_section.view_all.label</T>
        </Link>
      </div>
      <div className="block md:flex md:gap-6">
        <div className="flex-1 mb-5 md:mb-0">
          <ClosestEventCard
            course={courses[0]}
            onOpenModal={openModal}
            handleClick={() => handleCourseCardClick(courses[0])}
          />
        </div>
        <div className="flex-1 flex flex-col gap-6 justify-between">
          {courses.slice(1, 4).map((course, index) => (
            <div key={index} className="flex-grow flex-shrink-0">
              <LiveCourseCard course={course} onClick={() => handleCourseCardClick(course)} />
            </div>
          ))}
        </div>
      </div>
      {selectedCourse && (
        <BookingModal
          isOpen={!!selectedCourse}
          onClose={closeModal}
          course={selectedCourse}
          initialBookingSuccess={isSuccess}
        />
      )}
    </div>
  );
};

export default LiveCoursesSection;
